import React, { useState, useContext, useEffect } from 'react';
import styled from 'styled-components';
import Input from './Input';
import { Formik, Field, FormikHelpers } from 'formik';
import { TitleText } from './Text';
import { Button } from './Button';
import { AuthContext } from '../context/AuthContext';
import { get } from '../utils/get';
import { SCORECARD_URL } from '../api/urls';
import { GetHook, postEndpoint } from '../api/api';
import { BASE_API_URL } from '../utils/app-constants';
import Modal from './Modal';
import * as Yup from "yup";
import { toast } from 'react-toastify';
import { FileIcon } from '../assets/svg/FileIcon';
import useAuth from '../hooks/useAuth';

interface IProps {
  closeModal?: any;
  url?: any;
  scorecard_id?: string;
  comment_type?: string;
  onCommentAccessed?: () => void;
  onFileAttached?: () => void;
}

interface FormValues {
  comment: string;
  name: string;
  file: string;
}

const Comment = ({ closeModal, url, scorecard_id, comment_type, onCommentAccessed, onFileAttached }: IProps) => {
  const [scorecard, setScorecard] = useState([]);
  const [createRes, createReq] = postEndpoint();
  const [isOpen, setIsOpen] = useState(false);
  const [files, setFiles] = useState<any>([]);
  const auth = useAuth();
  const [isError, setIsError] = useState(false);

  const { data } = GetHook(
    `${SCORECARD_URL}/${scorecard_id}/summary`
  );

  useEffect(() => {
    setScorecard(data);
    return () => {};
  }, [data]);

  useEffect(() => {
    const { complete, pending } = createRes;
    if (!pending && complete) {
      closeModal();
    }
    return () => {};
  }, [createRes, closeModal]);

  useEffect(() => {
    if (onCommentAccessed) {
      onCommentAccessed();
    }
  }, [onCommentAccessed]);

  const handleFileAttachment = () => {
    if (onFileAttached) {
      onFileAttached();
    }
  };

  const ValidationSchema = Yup.object().shape({
    name: Yup.string().required('Document Name is required'),
  });

  const { authState } = auth;
  const PostComment = async (values: FormValues, formikHelpers: FormikHelpers<FormValues>) => {
    let payload: any;
    if (comment_type) {
      payload = new FormData();
      payload.append('comment_type', comment_type);
      payload.append(
        'target_user_id',
        scorecard && get(scorecard[0], 'owner_id')
      );
      payload.append('source_user_id', get(authState, 'user.id'));
      payload.append('comment', get(values, 'comment'));
      files.forEach(file => {
        payload.append('documents', get(file, 'file'));
        payload.append('document_name', get(file, 'file.name'));
      });
    } else {
      payload = {
        target_user_id: scorecard && get(scorecard[0], 'owner_id'),
        source_user_id: get(authState, 'user.id'),
        comment: get(values, 'comment')
      };
    }
    createReq(`${BASE_API_URL}/${url}/comment`, payload);
    formikHelpers.setSubmitting(false);
  };

  const handleDoneClick = () => {
    if (files.length > 0) {
      setIsOpen(false);
    } else {
      setIsError(true);
    }
  };

  const validateForm = (values: FormValues): boolean => {
    let filename = '';
    
    files.forEach(file => {
      values.file = get(file, 'file');
      filename = get(file, 'file.name');
    });
  
    const isCommentValid = !!values.comment.trim();
    const isFileValid = !!filename.trim();
  
    if (!isCommentValid && !isFileValid) {
      toast.error('You need to provide either a comment or attach an evidence file before proceeding.');
      return false;
    }
  
    if (isFileValid) {
      handleFileAttachment();
    }
  
    return true;
  };  

  return (
    <Formik<FormValues>
      initialValues={{ comment: '', name: '', file: '' }}
      onSubmit={(values, formikHelpers) => {
        if (validateForm(values)) {
          PostComment(values, formikHelpers);
        } else {
          formikHelpers.setSubmitting(false);
        }
      }}
    >
      {({ handleSubmit, setFieldValue, values }) => (
        <>
          <Container>
            <Field
              style={{ position: 'relative' }}
              name='comment'
              type='textarea'
              component={Input}
              width='90%'
              height='6em'
              isBordered={true}
              placeholder='Add New Comment '
            />
            {isOpen && (
              <Modal
                title='add documents'
                noFooter
                noHeader
                width='30%'
                closeModal={() => setIsOpen(false)}
              >
                <Formik
                  initialValues={{ name: '', file: '' }}
                  validationSchema={ValidationSchema}
                  onSubmit={() => {}}
                >
                  {({ setFieldValue, values }) => (
                    <FileUploadContainer>
                      <TitleText style={{ fontSize: 16 }}>
                        Add Documents
                      </TitleText>
                      <Documents>
                        {files &&
                          files.map((file, index) => {
                            return (
                              <>
                                <Text style={{ fontSize: 14, color: 'black' }}>
                                  {get(file, 'name')} ({get(file, 'file.name')})
                                </Text>{' '}
                                <span
                                  onClick={() =>
                                    setFiles(
                                      files.filter(
                                        (file, i) => files[i] !== files[index]
                                      )
                                    )
                                  }
                                >
                                  x
                                </span>
                              </>
                            );
                          })}
                      </Documents>
                      <Field
                        name='name'
                        type='text'
                        label='Document Name'
                        component={Input}
                        width='90%'
                        isBordered={true}
                        placeholder='Name'
                      />
                      <Label>
                        <TitleText style={{ color: '#01A8CE' }}>
                          +Add File
                        </TitleText>
                        <input
                          type='file'
                          name='file'
                          onChange={(e: any) => {
                            setFieldValue('file', e.target.files[0]);

                            const obj = {
                              name: get(values, 'name'),
                              file: e.target.files[0]
                            };
                            setFiles([...files, obj]);
                          }}
                          style={{ display: 'none' }}
                        />
                      </Label>
                      {isError && (
                        <div style={{ color: 'red' }}>Please fill out all required fields.</div>
                      )}
                      <EditButtons>
                        <EditButton
                          text='Cancel'
                          backgroundColor='#fff'
                          color='#214160'
                          onClick={() => setIsOpen(false)}
                        />
                        <EditButton
                          text='Done'
                          onClick={() => handleDoneClick()}
                        />
                      </EditButtons>
                    </FileUploadContainer>
                  )}
                </Formik>
              </Modal>
            )}
            {comment_type === 'measure_update' ? (
              <FileUploadIcon onClick={() => setIsOpen(true)}>
                <FileIcon />
              </FileUploadIcon>
            ) : null}
          </Container>
          <EditButton
            text='Cancel'
            backgroundColor='#fff'
            color='#214160'
            onClick={closeModal}
          />
          <PostButton
            backgroundColor='#D51067'
            text='Post'
            type='submit'
            onClick={() => handleSubmit()}
          />
        </>
      )}
    </Formik>
  );
};

export default Comment;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding: 1em;
`;

const Text = styled.p`
  font-size: 14px;
  font-weight: nomarl;
  margin: 0;
  align-items: center;
  line-height: 1.5em;
  color: ${props => props.color || '#ccc'};
`;

const EditButtons = styled.div`
  display: flex;
  flex-direction: row;
`;

const PostButton = styled(Button)`
  padding: 0.5em 2em;
  float: right;
  margin-right: 2rem;
`;

const EditButton = styled(Button)`
  margin-left: 1em;
  padding: 0.5em 1em;
`;

const FileUploadIcon = styled.span`
  position: absolute;
  right: 4.75em;
  bottom: 4.5em;
  cursor: pointer;
`;

const FileUploadContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  display: flex;
  flex-direction: column;
  color: #01a8ce;
  font-family: 'Raleway', sans-serif;
  font-size: 0.8em;
  margin: 0.5em 0;
  position: relative;
`;

const Documents = styled.div`
  display: grid;
  grid-template-columns: 2fr 0.5fr;
  row-gap: 0.75em;
  padding-top: 1em;
  padding-bottom: 1em;
  span {
    cursor: pointer;
  }
`;